import { IN_FORCE_STATUSES, isCancellationStatus, matchesCarrierFamily } from "@shared/schema";
import type { CancellationReason, Policy } from "@shared/schema";
import { formatPolicySubstatusLabel, POLICY_SUBSTATUSES } from "@shared/policy-substatus";

const STATUS_BADGE_CLASSES: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-200",
  "pending cancel": "bg-orange-100 text-orange-800 border-orange-200",
  renewing: "bg-blue-100 text-blue-800 border-blue-200",
  reinstated: "bg-teal-100 text-teal-800 border-teal-200",
  "non-renewal": "bg-amber-100 text-amber-800 border-amber-200",
  expired: "bg-gray-100 text-gray-800 border-gray-200",
  renewed: "bg-indigo-100 text-indigo-800 border-indigo-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  replaced: "bg-purple-100 text-purple-800 border-purple-200",
  // CSR-1042 — Progressive "Renewal Offer". Violet (distinct from purple,
  // which is owned by "replaced") signals a transitional / not-yet-bound
  // renewal state.
  "renewal offer": "bg-violet-100 text-violet-800 border-violet-200",
  // CSR-1725/CSR-1699 — "Flat Cancel" is a cancellation-family status
  // (policy voided from inception). Same red family as "cancelled", kept
  // visually distinct is unnecessary since both are terminal cancel states.
  "flat cancel": "bg-red-100 text-red-800 border-red-200",
};

const DEFAULT_STATUS_BADGE = "bg-gray-100 text-gray-600 border-gray-200";

// CSR-895: canonical status value -> human display label. Values are
// lowercased to match the normalized form written by `server/sync.ts`
// (`normalizeStatus`). Entries here override the default title-case
// formatter where the human label differs from a per-word title-cased
// version of the canonical value (e.g. "pending cancel" surfaces as
// "Pending Cancellation"). Keep this list aligned with the dropdowns in
// `PolicyEdit.tsx` and `Policies.tsx` (create dialog).
const STATUS_DISPLAY_LABELS: Record<string, string> = {
  active: "Active",
  "pending cancel": "Pending Cancellation",
  expired: "Expired",
  cancelled: "Cancelled",
  renewing: "Renewing",
  renewed: "Renewed",
  "non-renewal": "Non-Renewal",
  // CSR-1042 — canonical label for the Progressive "Renewal Offer" status.
  // Surfaces verbatim in dropdowns, badges, and filters.
  "renewal offer": "Renewal Offer",
  // CSR-1725/CSR-1699 — canonical label for "Flat Cancel".
  "flat cancel": "Flat Cancel",
};

export const POLICY_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "active", label: STATUS_DISPLAY_LABELS["active"] },
  { value: "pending cancel", label: STATUS_DISPLAY_LABELS["pending cancel"] },
  { value: "expired", label: STATUS_DISPLAY_LABELS["expired"] },
  { value: "cancelled", label: STATUS_DISPLAY_LABELS["cancelled"] },
  { value: "renewing", label: STATUS_DISPLAY_LABELS["renewing"] },
  { value: "renewed", label: STATUS_DISPLAY_LABELS["renewed"] },
  // CSR-2336 — "non-renewal" is no longer selectable as a main status; it is a
  // substatus (POLICY_SUBSTATUS_OPTIONS). Its label stays above so a legacy
  // row that still carries it renders readably.
  // CSR-1042 — keeps the renewal family at the tail of the dropdown.
  { value: "renewal offer", label: STATUS_DISPLAY_LABELS["renewal offer"] },
  // CSR-1725/CSR-1699 — "Flat Cancel" (policy voided from inception). Added
  // as a selectable manual-status option ahead of DC's own CSR-1699 rollout;
  // see server/sync.ts normalizeStatus for the matching sync-side mapping.
  { value: "flat cancel", label: STATUS_DISPLAY_LABELS["flat cancel"] },
];

export const POLICY_SUBSTATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  POLICY_SUBSTATUSES.map((value) => ({ value, label: formatPolicySubstatusLabel(value) }));

export function getPolicyStatusBadgeClass(status?: string | null): string {
  if (!status) return DEFAULT_STATUS_BADGE;
  return STATUS_BADGE_CLASSES[status.toLowerCase()] ?? DEFAULT_STATUS_BADGE;
}

export function formatPolicyStatusLabel(status?: string | null): string {
  if (!status) return "—";
  const key = status.toLowerCase();
  const override = STATUS_DISPLAY_LABELS[key];
  if (override) return override;
  return status
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// CSR-1978: does this term currently provide coverage? Membership test over the
// canonical `IN_FORCE_STATUSES` allow-list (`@shared/schema`) — anything absent
// from it (cancelled, flat cancel, expired, renewed, replaced, renewal offer) is
// out of force by omission, so a status added there is honoured here for free.
// Statuses are stored lowercased by `server/sync.ts`; normalized anyway for the
// manually-entered path.
export function isPolicyInForce(status?: string | null): boolean {
  if (!status) return false;
  return IN_FORCE_STATUSES.includes(
    status.toLowerCase() as (typeof IN_FORCE_STATUSES)[number],
  );
}

export function sortPoliciesByEffectiveDate(policies: Policy[]): Policy[] {
  return [...policies].sort((a, b) =>
    (b.effectiveDate || "").localeCompare(a.effectiveDate || "")
  );
}

export function formatPolicyPremium(premium?: string | null): string {
  if (!premium) return "—";
  const amount = Number(premium);
  if (!Number.isFinite(amount)) return "—";
  return `$${amount.toLocaleString()}`;
}

// CSR-840: statuses for which the Cancellation Date is surfaced on the
// insured profile's Policies tab (list column + detail header). Status values
// are stored lowercased by `server/sync.ts` (`"pending cancellation"` from DC
// is normalized to `"pending cancel"`), so we normalize here too.
export function shouldShowCancellationDate(status?: string | null): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return (
    s === "expired" || s === "cancelled" || s === "pending cancel" || s === "flat cancel"
  );
}

// CSR-1901 (CSR-1672) — human labels for the DC cancellation-reason enum.
// Keyed on the mirrored literal type so a new DC value without a label fails
// `tsc` (CSR-2264); an unknown token at runtime still falls back to "—".
const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
  non_pay: "Non-Payment",
  underwriting: "Underwriting",
  insured_request: "Insured Request",
  other: "Other",
};

export function formatCancellationReasonLabel(reason?: string | null): string {
  if (!reason) return "—";
  return CANCELLATION_REASON_LABELS[reason as CancellationReason] ?? "—";
}

type CancellationReasonPolicy = Pick<
  Policy,
  "status" | "carrier" | "parentCarrier" | "cancellationReason" | "statusReason"
>;

export function getRawCancellationReason(
  policy: CancellationReasonPolicy,
): string | null {
  // statusReason also carries non-cancellation banners, such as term expiry.
  if (!isCancellationStatus(policy.status?.toLowerCase())) return null;
  if (!matchesCarrierFamily(policy.carrier, policy.parentCarrier, "Geico"))
    return null;
  return policy.statusReason?.trim() || null;
}

export function hasCancellationReason(
  policy: CancellationReasonPolicy,
): boolean {
  return Boolean(policy.cancellationReason || getRawCancellationReason(policy));
}

export function formatPolicyCancellationReason(
  policy: CancellationReasonPolicy,
): string {
  return (
    getRawCancellationReason(policy) ??
    formatCancellationReasonLabel(policy.cancellationReason)
  );
}
